import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import CertificadosExternosService from '@/services/certificadosExternos.service'
import { convertTime, convertHHHmm, convertHMS, newDialog, toast, i18N } from '@/common/util'

import type { ExternalCertificateType, LoaderType } from '@/types/types'

export interface DenyType {
  id: string
  justification?: number
  reason?: string
}

const typeJustification = {
  IncompativelComFuncao: 1,
  CertificadoInvalido: 2,
  CertificadoIlegivel: 3,
  Outro: 4
}

export const useCertificadosExternos = defineStore('CertificadosExternos', () => {
  const list = ref<ExternalCertificateType[]>([])
  const adminList = ref<ExternalCertificateType[]>([])

  const status = reactive<Record<string, LoaderType>>({
    getAll: 'initial',
    delete: 'initial',
    getById: 'initial',
    reply: 'initial',
    save: 'initial'
  })

  const loading = computed(() => Object.values(status).some((el) => el === 'loading'))

  const save = async (objData: ExternalCertificateType) => {
    status.save = 'loading'

    try {
      let result = null

      if (!objData?.id) result = await CertificadosExternosService.post(null, getFormDataSubmit(objData), null, true)
      else result = await CertificadosExternosService.put(null, getFormDataSubmit(objData), null, true)

      if (result?.success && result?.data) {
        toast.success({ message: i18N(objData?.id ? 'toasts.createSuccess' : 'toasts.updateSuccess') })
        getAll(objData.userId)

        status.save = 'success'
        return true
      }
    } catch (error) {
      //
    }

    status.save = 'error'
    return false
  }

  const getAll = async (userId?: string) => {
    if(!userId) return 
    status.getAll = 'loading'
    try {
      const { data } = await CertificadosExternosService.getByUserList(userId)
      list.value = data.map((el) => getFormatResponse(el))

      status.getAll = 'success'
    } catch {
      status.getAll = 'error'
    }
  }

  const getById = async (id: string) => {
    status.getById = 'loading'
    try {
      const { data } = await CertificadosExternosService.get(id)
      status.getById = 'success'

      return getFormatResponse(data)
    } catch {
      status.getById = 'error'
    }
  }

  const remove = async (data: ExternalCertificateType, force?: boolean) => {
    if (!force)
      return newDialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.externalCertificate').toLowerCase(), name: data.name}),
        complete: () => remove(data, true)
      })

    try {
      status.delete = 'loading'

      const { success } = await CertificadosExternosService.delete(data.id)
      if (success) {
        toast.success({message: i18N('toasts.removeSuccess')})
        getAll(data.userId)
      }

      status.delete = 'success'
      return success
    } catch {
      status.delete = 'error'
    }
  }

  const sendReplyRequest = async (objData: DenyType, approved: boolean) => {
    status.reply = 'loading'
    try {
      const { success } = await CertificadosExternosService.sendReplyRequest(objData, approved)
      if (success) {
        toast.success({message: i18N('toasts.sentSuccess')})
        getAdminList()
      }
      status.reply = 'success'
      return success
    } catch {
      status.reply = 'error'
    }
  }

  const getAdminList = async () => {
    try {
      status.getAll = 'loading'

      const { data, success } = await CertificadosExternosService.getAdminList()
      if (success) {
        adminList.value = data.map((el) => getFormatResponse(el))
        status.getAll = 'success'
      }
    } catch (error) {
      status.getAll = 'error'
    }
  }

  const getFormDataSubmit = (objData: ExternalCertificateType): FormData => {
    const formData = new FormData()

    objData.id && formData.append('id', objData.id)

    formData.append('usuarioId', objData.userId)
    formData.append('descricao', objData.name)
    formData.append('tipo', objData.type)
    formData.append('outroTipo', objData.descriptionOtherType || '')
    formData.append('instituicao', objData.institution)
    formData.append('cargaHoraria', String(convertTime(`${objData.duration}:00`)))
    formData.append('dataInicio', new Date(objData.initDate).toISOString())
    formData.append('dataFim', new Date(objData.endDate).toISOString())
    formData.append('certificado', objData.certificate)
    formData.append('certificadoUrl', objData.certificateUrl || '')
    formData.append('linkCredencial', objData.credentialUrl || '')
    formData.append('codigoCredencial', objData.credentialCode || '')

    return formData
  }

  const getFormatResponse = (objData: any): ExternalCertificateType => {
    return {
      id: objData.id,
      userId: objData.usuarioId,
      name: objData.descricao,
      type: objData.tipo,
      descriptionOtherType: objData.outroTipo,
      institution: objData.instituicao,
      duration: convertHHHmm(convertHMS(objData.cargaHoraria)),
      initDate: objData.dataInicio,
      endDate: objData.dataFim,
      certificate: objData.certificado,
      certificateUrl: objData.certificadoUrl,
      status: objData.status,
      credentialCode: objData.codigoCredencial,
      credentialUrl: objData.linkCredencial,
      userName: objData?.usuario?.nomeCompleto || '',
      statusRequest: objData.statusAprovacao,
      createdAt: objData.createdAt,
      reason: objData.descricaoJustificativa,
      justification: typeJustification[objData.tipoJustificativa] || ''
    }
  }

  return {
    list,
    adminList,
    // Getters
    loading,
    // Actions
    save,
    getAll,
    remove,
    getById,
    sendReplyRequest,
    getAdminList
  }
})

export default useCertificadosExternos
